// @flow

import { connect } from "react-redux";
import React, { useRef, useEffect } from "react";
import type { Header } from "@tanstack/react-table";

import {
  HeaderItem as StyledHeaderItem,
  SeqNo as StyledSeqNo,
  Title
} from "./styles";
import Resizer from "./Resizer";
import Sort from "./Filter/Sort";
import Priority from "./Filter/Priority";
import Status from "./Filter/Status";
import Date from "./Filter/Date";
import User from "./Filter/User";
import Chatroom from "./Filter/Chatroom";
import { processColumnNames } from "src/constants/display";
import useBoolean from "src/hooks/useBoolean";
import { getInstanceFilterId } from "src/reducers";
import { updateColumnWidth } from "src/actions/fieldSizeMap";
import Icon from "src/icons";

import type { AppState, ColumnId, Action } from "src/types";

type Props = {
  id: string,
  column: string,
  setFilter: Function,
  toggleSortBy: Function,
  filter: Array<any>,
  header: Header,
  _updateColumnWidth: (columnId: ColumnId, width: string) => Action
};

const HeaderItem = ({
  id,
  column,
  setFilter,
  filter,
  toggleSortBy,
  header,
  _updateColumnWidth
}: Props) => {
  const parentRef = useRef(null);
  const {
    value: dropdown,
    toggleBoolean: toggleDropdown,
    setFalse: closeDropdown
  } = useBoolean();

  // Open filter dropdown only if the header item is clicked
  const handleHeaderClick = e => {
    toggleDropdown(e);
  };

  // Update the widths of the columns
  useEffect(() => {
    _updateColumnWidth(header.column.id, header.getSize());
  }, [header.getSize()]);

  const label = processColumnNames[column];
  switch (column) {
    case "seqNo":
      return (
        <StyledSeqNo onClick={handleHeaderClick} ref={parentRef} type={column}>
          <span>{label}</span>
          <Icon type="triangleDown" />
          {dropdown && (
            <Sort
              column={column}
              handleClose={closeDropdown}
              parentRef={parentRef}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledSeqNo>
      );

    case "title":
      return (
        <Title
          isResizing={header.column.getIsResizing()}
          width={header.getSize()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Sort
              column={column}
              handleClose={closeDropdown}
              parentRef={parentRef}
              toggleSortBy={toggleSortBy}
            />
          )}
        </Title>
      );

    case "age":
      return (
        <StyledHeaderItem
          width={header.getSize()}
          isResizing={header.column.getIsResizing()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Sort
              column={column}
              handleClose={closeDropdown}
              id={id}
              parentRef={parentRef}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );

    case "priority":
      return (
        <StyledHeaderItem
          isResizing={header.column.getIsResizing()}
          width={header.getSize()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Priority
              handleClose={closeDropdown}
              parentRef={parentRef}
              setFilter={setFilter}
              filter={filter}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );

    case "status":
      return (
        <StyledHeaderItem
          isResizing={header.column.getIsResizing()}
          width={header.getSize()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Status
              handleClose={closeDropdown}
              id={id}
              parentRef={parentRef}
              setFilter={setFilter}
              filter={filter}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );

    case "dueDate":
      return (
        <StyledHeaderItem
          isResizing={header.column.getIsResizing()}
          width={header.getSize()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Date
              column={column}
              handleClose={closeDropdown}
              parentRef={parentRef}
              setFilter={setFilter}
              filter={filter}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );

    case "createdAt":
    case "updatedAt":
    case "completedAt":
      return (
        <StyledHeaderItem
          isResizing={header.column.getIsResizing()}
          width={header.getSize()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Date
              column={column}
              handleClose={closeDropdown}
              parentRef={parentRef}
              setFilter={setFilter}
              filter={filter}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );

    case "owner":
    case "creator":
    case "members":
    case "completedBy":
      return (
        <StyledHeaderItem
          width={header.getSize()}
          isResizing={header.column.getIsResizing()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <User
              column={column}
              handleClose={closeDropdown}
              parentRef={parentRef}
              setFilter={setFilter}
              filter={filter}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );

    case "parent":
      return (
        <StyledHeaderItem
          isResizing={header.column.getIsResizing()}
          width={header.getSize()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Chatroom
              column={column}
              handleClose={closeDropdown}
              parentRef={parentRef}
              setFilter={setFilter}
              filter={filter}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );

    default:
      return (
        <StyledHeaderItem
          isResizing={header.column.getIsResizing()}
          width={header.getSize()}
          onClick={handleHeaderClick}
          ref={parentRef}
          type={column}
        >
          <span>{label}</span>
          <Icon type="triangleDown" />
          <Resizer header={header} />
          {dropdown && (
            <Sort
              column={column}
              handleClose={closeDropdown}
              parentRef={parentRef}
              toggleSortBy={toggleSortBy}
            />
          )}
        </StyledHeaderItem>
      );
  }
};

const mapStateToProps = ({ app }: { app: AppState }) => ({
  id: getInstanceFilterId(app)
});

export default connect(mapStateToProps, {
  _updateColumnWidth: updateColumnWidth
})(HeaderItem);
